/**
 * QuickBookingWidget - Premium mobile-first booking component
 * 
 * A high-conversion booking widget designed for funnel Thank You pages.
 * Styled for Safe Money Guys brand - premium financial consultation experience.
 * 
 * Features:
 * - Shows today/tomorrow slots (max 6, prioritizing same-day)
 * - Lightweight confirmation modal
 * - Slot conflict handling with auto-refresh
 * - Success confirmation screen
 * - Fallback link to full calendar
 * - Funnel integration: emits booking events to funnel runtime (MA-ARCH-082)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Clock, ChevronRight, Calendar, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

const API_URL = window.location.origin || '';

// MA-FUNNEL-DARK-THEME-FIX-033: Theme-aware BRAND constants
// Derive from tokens when provided, fallback to Safe Money Guys brand values
const getBrandColors = (tokens = {}, accentColor = null) => {
  const primary = accentColor || tokens.primary || '#63B3ED';
  const primaryDark = tokens.primaryDark || (primary === '#63B3ED' ? '#4299E1' : darken(primary, 10));
  
  return {
    primary,
    primaryDark,
    primaryLight: '#EBF8FF',
    textPrimary: tokens.textPrimary || '#1F2937',      // ← Now adapts to theme
    textSecondary: tokens.textSecondary || '#6B7280',  // ← Now adapts to theme
    cardBg: tokens.cardBg || tokens.surface_elevated || '#FFFFFF',
    pageBg: tokens.containerBg || tokens.surface_base || '#F8FAFC',
    border: tokens.borderSubtle || '#E5E7EB',
    successBg: '#DCFCE7',  // Keep success colors static (always green)
    successText: '#16A34A',
  };
};

// Helper for darken function (if not imported from theme engine)
const darken = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

// MA-FUNNEL-ARCH-091: Helper for lighten function (derived surface adjustment)
const lighten = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

// MA-BOOK-002: Default configuration - NO hardcoded calendar/location IDs
// These are presentation defaults only, not production routing defaults
const DEFAULT_CONFIG = {
  // REMOVED: calendarId and locationId - these MUST be explicitly provided
  // Silent fallback to hardcoded IDs is unsafe and has been removed
  timezone: null,
  appointmentTitle: 'Mortgage Protection Review',
  fallbackText: 'Need another time? View full calendar',
};

/**
 * Get the browser's timezone or fall back to a default
 */
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'America/New_York';
  }
};

/**
 * Get a friendly display name for a timezone
 * Returns simplified names for common US timezones, or null for uncommon ones
 */
const getTimezoneDisplayName = (timezone) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    const longName = tzPart?.value || '';
    
    // Simplify common US timezone names
    const simplifications = {
      'Eastern Standard Time': 'Eastern Time',
      'Eastern Daylight Time': 'Eastern Time',
      'Central Standard Time': 'Central Time',
      'Central Daylight Time': 'Central Time',
      'Mountain Standard Time': 'Mountain Time',
      'Mountain Daylight Time': 'Mountain Time',
      'Pacific Standard Time': 'Pacific Time',
      'Pacific Daylight Time': 'Pacific Time',
      'Alaska Standard Time': 'Alaska Time',
      'Alaska Daylight Time': 'Alaska Time',
      'Hawaii-Aleutian Standard Time': 'Hawaii Time',
      'Hawaii Standard Time': 'Hawaii Time',
    };
    
    return simplifications[longName] || null; // Return null for uncommon timezones
  } catch (e) {
    return null;
  }
};

/**
 * Widget States
 * MA-BOOK-002: Added NOT_CONFIGURED state for explicit error handling
 */
const WIDGET_STATE = {
  LOADING: 'loading',
  SLOTS: 'slots',
  CONFIRMING: 'confirming',
  BOOKING: 'booking',
  SUCCESS: 'success',
  ERROR: 'error',
  NO_SLOTS: 'no_slots',
  NOT_CONFIGURED: 'not_configured',  // MA-BOOK-002: Calendar not configured
};

/**
 * Primary Slot Button - For the recommended/first available slot
 * Premium styling: subtle background tint, refined badge, soft elevation
 * MA-FUNNEL-ARCH-091: Uses theme.selectedSlotBg for dedicated selected slot surface
 */
const PrimarySlotButton = ({ slot, onSelect, disabled, durationText, theme, sizes = {} }) => {
  // MA-FUNNEL-ARCH-077: Use sizes for compact mode compatibility
  const isCompact = sizes.timeSlot === 'text-[7px]';
  
  return (
    <button
      data-testid={`slot-btn-${slot.slot_id}`}
      onClick={() => onSelect(slot)}
      disabled={disabled}
      className={`${isCompact ? '' : 'w-full'} font-medium ${isCompact ? 'text-[7px]' : 'text-[13px]'} relative active:scale-[0.98] transition-all duration-[120ms] ease-out disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2`}
      style={{ 
        color: theme.textPrimary, 
        borderRadius: isCompact ? '6px' : theme.slotRadius,
        backgroundColor: theme.selectedSlotBg,
        border: `${isCompact ? '1px' : '1.5px'} solid ${theme.accentBorder}`,
        boxShadow: isCompact ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.05)',
        padding: isCompact ? '4px 6px' : '10px 12px',
        ...(isCompact && { width: '100%' }),  // Full width in compact mode
      }}
    >
      <div className={`flex items-center justify-between`}>
        <div className={`flex items-center ${isCompact ? 'gap-1' : 'gap-2'}`}>
          <Clock className={isCompact ? 'w-2.5 h-2.5' : 'w-4 h-4'} style={{ color: theme.accent }} />
          <span className="font-semibold">{slot.display_time}</span>
          {durationText && (
            <span 
              className={`${isCompact ? 'text-[5px]' : 'text-[10px]'} rounded`}
              style={{ color: theme.textMuted, padding: isCompact ? '1px 3px' : '2px 6px', backgroundColor: 'rgba(0,0,0,0.05)' }}
              data-testid="slot-duration"
            >
              {durationText}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span 
            className="font-medium rounded-full"
            style={{ 
              fontSize: isCompact ? '4px' : '9px',
              padding: isCompact ? '1px 3px' : '2px 6px',
              backgroundColor: theme.accentBadgeBg,
              color: theme.accent, 
              border: `1px solid ${theme.accentBadgeBorder}`
            }}
            data-testid="recommended-badge"
          >
            Recommended
          </span>
          <ChevronRight className={isCompact ? 'w-2 h-2' : 'w-4 h-4'} style={{ color: theme.accent }} />
        </div>
      </div>
    </button>
  );
};

/**
 * Secondary Slot Button - For other available slots
 * Can optionally show "Recommended" badge for first tomorrow slot when no today slots
 * MA-UI-100: Added durationText prop for manual duration display
 * MA-FUNNEL-ARCH-091: Uses theme.slotBg for dedicated slot surface, theme.selectedSlotBg for recommended
 */
const SecondarySlotButton = ({ slot, onSelect, disabled, isRecommended = false, durationText, theme, sizes = {} }) => {
  // MA-FUNNEL-ARCH-077: Use sizes for compact mode compatibility
  const isCompact = sizes.timeSlot === 'text-[7px]';
  const slotRef = useRef(null);
  
  // MA-FUNNEL-ARCH-093: Inspect computed styles for visual audit
  useEffect(() => {
    if (slotRef.current && !isRecommended) {
      const computed = window.getComputedStyle(slotRef.current);
      const parent = slotRef.current.parentElement;
      const parentComputed = parent ? window.getComputedStyle(parent) : null;
      
      const surface = isCompact ? 'PPB Funnel Step' : 'FB Canvas (or Runtime)';
      
      console.log(`[MA-FUNNEL-ARCH-093] ${surface} Regular Slot Computed Styles:`, {
        surface,
        isCompact,
        element: {
          backgroundColor: computed.backgroundColor,
          color: computed.color,
          opacity: computed.opacity,
          filter: computed.filter,
          mixBlendMode: computed.mixBlendMode,
          boxShadow: computed.boxShadow,
          border: computed.border,
          borderColor: computed.borderColor,
          borderOpacity: computed.borderOpacity,
        },
        parent: parentComputed ? {
          backgroundColor: parentComputed.backgroundColor,
          opacity: parentComputed.opacity,
          filter: parentComputed.filter,
          mixBlendMode: parentComputed.mixBlendMode,
        } : null,
        inlineStyle: {
          backgroundColor: slotRef.current.style.backgroundColor,
          opacity: slotRef.current.style.opacity,
          filter: slotRef.current.style.filter,
        },
        className: slotRef.current.className,
        themeSlotBg: theme.slotBg,
        themeTextPrimary: theme.textPrimary,
        themeTextMuted: theme.textMuted,
      });
    }
  }, [isCompact, isRecommended, theme.slotBg]);
  
  return (
    <button
      ref={slotRef}
      data-testid={`slot-btn-${slot.slot_id}`}
      onClick={() => onSelect(slot)}
      disabled={disabled}
      className={cn(
        isCompact ? '' : 'w-full',
        "font-medium",
        isCompact ? 'text-[7px]' : 'text-[13px]',
        "relative",
        "active:scale-[0.98] transition-all duration-[120ms] ease-out",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "focus:outline-none focus:ring-2 focus:ring-offset-2"
      )}
      style={{ 
        color: theme.textPrimary, 
        borderRadius: isCompact ? '6px' : theme.slotRadius,
        padding: isCompact ? '4px 6px' : '10px 12px',
        backgroundColor: isRecommended ? theme.selectedSlotBg : theme.slotBg,
        border: isRecommended ? `${isCompact ? '1px' : '1.5px'} solid ${theme.accentBorder}` : `1px solid ${theme.textMuted}30`,
        boxShadow: isRecommended ? (isCompact ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.05)') : '0 1px 2px rgba(0,0,0,0.03)',
        ...(isCompact && { width: '100%' }),  // Full width in compact mode
      }}
    >
      <div className="flex items-center justify-between">
        <div className={`flex items-center ${isCompact ? 'gap-1' : 'gap-2'}`}>
          <Clock className={isCompact ? 'w-2.5 h-2.5' : 'w-4 h-4'} style={{ color: isRecommended ? theme.accent : theme.textMuted }} />
          <span className={isRecommended ? 'font-semibold' : ''}>{slot.display_time}</span>
          {durationText && (
            <span 
              className={`${isCompact ? 'text-[5px]' : 'text-[10px]'} rounded`}
              style={{ color: theme.textMuted, padding: isCompact ? '1px 3px' : '2px 6px', backgroundColor: 'rgba(0,0,0,0.05)' }}
            >
              {durationText}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isRecommended && (
            <span 
              className="font-medium rounded-full"
              style={{ 
                fontSize: isCompact ? '4px' : '9px',
                padding: isCompact ? '1px 3px' : '2px 6px',
                backgroundColor: theme.accentBadgeBg,
                color: theme.accent, 
                border: `1px solid ${theme.accentBadgeBorder}`
              }}
              data-testid="recommended-badge"
            >
              Recommended
            </span>
          )}
          <ChevronRight className={isCompact ? 'w-2 h-2' : 'w-4 h-4'} style={{ color: isRecommended ? theme.accent : theme.textMuted }} />
        </div>
      </div>
    </button>
  );
};

/**
 * Loading State Component - Modern premium design
 * MA-BOOKINGSTEP-APPEARANCE-FIX-034: Accept BRAND as prop (no longer global)
 */
const LoadingState = ({ BRAND }) => (
  <div 
    className="flex flex-col items-center justify-center py-16 space-y-4" 
    data-testid="loading-state"
  >
    <div 
      className="w-14 h-14 rounded-full flex items-center justify-center"
      style={{ backgroundColor: BRAND.primaryLight }}
    >
      <Loader2 className="w-7 h-7 animate-spin" style={{ color: BRAND.primaryDark }} />
    </div>
    <div className="text-center">
      <p className="text-sm font-medium" style={{ color: BRAND.textPrimary }}>
        Loading available times...
      </p>
      <p className="text-xs mt-1" style={{ color: BRAND.textSecondary }}>
        This will only take a moment
      </p>
    </div>
  </div>
);

/**
 * Error State Component
 * MA-BOOKINGSTEP-APPEARANCE-FIX-034: Accept BRAND as prop
 */
const ErrorState = ({ message, onRetry, BRAND }) => (
  <div 
    className="flex flex-col items-center justify-center py-16 space-y-4" 
    data-testid="error-state"
  >
    <div 
      className="w-14 h-14 rounded-full flex items-center justify-center"
      style={{ backgroundColor: '#FEE2E2' }}
    >
      <AlertCircle className="w-7 h-7 text-red-500" />
    </div>
    <div className="text-center">
      <p className="text-sm font-medium" style={{ color: BRAND.textPrimary }}>
        Something went wrong
      </p>
      <p className="text-xs mt-1 max-w-xs" style={{ color: BRAND.textSecondary }}>
        {message}
      </p>
    </div>
    <button 
      onClick={onRetry} 
      data-testid="retry-btn"
      className="text-sm font-medium px-5 py-2.5 rounded-xl border border-[#E5E7EB] hover:bg-[#EBF8FF] hover:border-[#63B3ED] transition-all"
      style={{ color: BRAND.textPrimary }}
    >
      Try Again
    </button>
  </div>
);

/**
 * No Slots State Component
 * MA-BOOKINGSTEP-APPEARANCE-FIX-034: Accept BRAND as prop
 */
const NoSlotsState = ({ fallbackUrl, fallbackText, BRAND }) => (
  <div 
    className="flex flex-col items-center justify-center py-16 space-y-4" 
    data-testid="no-slots-state"
  >
    <div 
      className="w-14 h-14 rounded-full flex items-center justify-center"
      style={{ backgroundColor: BRAND.primaryLight }}
    >
      <Calendar className="w-7 h-7" style={{ color: BRAND.primaryDark }} />
    </div>
    <div className="text-center">
      <p className="text-sm font-medium" style={{ color: BRAND.textPrimary }}>
        No times available
      </p>
      <p className="text-xs mt-1" style={{ color: BRAND.textSecondary }}>
        No appointments available for today or tomorrow.
      </p>
    </div>
    {fallbackUrl && (
      <a
        href={fallbackUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm font-medium px-5 py-2.5 rounded-xl hover:bg-[#EBF8FF] transition-all inline-flex items-center gap-1"
        style={{ color: BRAND.primaryDark, border: `1px solid ${BRAND.primary}` }}
        data-testid="fallback-link"
      >
        View full calendar
        <svg 
          className="w-3.5 h-3.5" 
          viewBox="0 0 12 12" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 10L10 2M10 2H4M10 2V8" />
        </svg>
      </a>
    )}
  </div>
);

/**
 * MA-BOOK-002: Not Configured State Component
 * Shown when booking step has no calendar assigned
 * This replaces silent fallback to hardcoded calendar IDs
 * MA-BOOKINGSTEP-APPEARANCE-FIX-034: Accept BRAND as prop
 */
const NotConfiguredState = ({ BRAND }) => (
  <div 
    className="flex flex-col items-center justify-center py-16 space-y-4" 
    data-testid="not-configured-state"
  >
    <div 
      className="w-14 h-14 rounded-full flex items-center justify-center"
      style={{ backgroundColor: '#FEF3C7' }}
    >
      <AlertCircle className="w-7 h-7" style={{ color: '#D97706' }} />
    </div>
    <div className="text-center">
      <p className="text-sm font-medium" style={{ color: BRAND.textPrimary }}>
        Booking Not Available
      </p>
      <p className="text-xs mt-1 max-w-[240px]" style={{ color: BRAND.textSecondary }}>
        This booking step is not configured. Please contact us to schedule your appointment.
      </p>
    </div>
  </div>
);

/**
 * Success State Component
 * MA-BOOKINGSTEP-APPEARANCE-FIX-034: Accept BRAND as prop
 */
const SuccessState = ({ displayDate, displayTime, appointmentTitle, BRAND }) => (
  <div 
    className="flex flex-col items-center justify-center py-12 space-y-5" 
    data-testid="success-state"
  >
    <div 
      className="w-16 h-16 rounded-full flex items-center justify-center"
      style={{ backgroundColor: BRAND.successBg }}
    >
      <CheckCircle2 className="w-9 h-9" style={{ color: BRAND.successText }} />
    </div>
    
    <div className="text-center space-y-1">
      <h2 className="text-xl font-semibold" style={{ color: BRAND.textPrimary }}>You're Booked!</h2>
      <p className="text-sm" style={{ color: BRAND.textSecondary }}>{appointmentTitle}</p>
    </div>
    
    <div 
      className="rounded-xl px-6 py-3 text-center"
      style={{ backgroundColor: BRAND.pageBg }}
    >
      <p className="text-lg font-semibold" style={{ color: BRAND.textPrimary }}>
        {displayDate} at {displayTime}
      </p>
    </div>
    
    <p className="text-xs" style={{ color: BRAND.textSecondary }}>
      You'll receive a confirmation text shortly.
    </p>
  </div>
);

/**
 * Confirmation View Component - Inline confirmation (replaces slots view)
 * No modals or fixed positioning - renders directly in the widget flow
 * Most reliable approach for Safari iframes and embedded contexts
 * MA-BOOKINGSTEP-APPEARANCE-FIX-034: Accept BRAND as prop
 */
const ConfirmationView = ({
  slot,
  onConfirm,
  onCancel,
  isBooking,
  appointmentTitle,
  BRAND,
}) => {
  if (!slot) return null;
  
  const displayDate = slot.is_today ? 'Today' : 'Tomorrow';
  
  return (
    <div 
      className="flex flex-col items-center justify-center py-8 space-y-6"
      data-testid="confirmation-view"
    >
      {/* Calendar icon */}
      <div 
        className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{ backgroundColor: BRAND.primaryLight }}
      >
        <Calendar className="w-8 h-8" style={{ color: BRAND.primaryDark }} />
      </div>
      
      {/* Header */}
      <div className="text-center space-y-2">
        <h3 className="text-xl font-semibold" style={{ color: BRAND.textPrimary }}>
          Confirm Your Time
        </h3>
        
        {/* Secure booking indicator with lock icon */}
        <div className="flex items-center justify-center gap-1.5">
          <Lock className="w-3.5 h-3.5" style={{ color: BRAND.primary }} />
          <span className="text-xs font-medium" style={{ color: BRAND.primary }}>
            Secure booking
          </span>
        </div>
      </div>
      
      {/* Selected time card */}
      <div 
        className="w-full rounded-xl px-6 py-4 text-center"
        style={{ 
          backgroundColor: '#F2F9FF',
          border: '2px solid #63B3ED'
        }}
      >
        <p className="text-lg font-semibold" style={{ color: BRAND.primaryDark }}>
          {displayDate} at {slot.display_time}
        </p>
      </div>
      
      {/* Appointment details */}
      <div className="text-center">
        <p className="text-sm font-medium" style={{ color: BRAND.textPrimary }}>{appointmentTitle}</p>
        <p className="text-xs mt-1" style={{ color: BRAND.textSecondary }}>Your review takes about 15 minutes.</p>
      </div>
      
      {/* Action buttons */}
      <div className="w-full space-y-3 pt-2">
        <Button
          data-testid="confirm-booking-btn"
          onClick={onConfirm}
          disabled={isBooking}
          className="w-full py-6 text-base font-semibold rounded-xl"
          style={{ 
            backgroundColor: BRAND.primaryDark, 
            color: '#FFFFFF',
          }}
        >
          {isBooking ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Booking your time...
            </span>
          ) : (
            'Yes, book this time'
          )}
        </Button>
        
        <button
          data-testid="cancel-booking-btn"
          onClick={onCancel}
          disabled={isBooking}
          className="w-full py-3 text-sm font-medium rounded-xl border transition-colors"
          style={{ 
            color: BRAND.textSecondary,
            borderColor: BRAND.border,
            background: 'transparent',
          }}
          onMouseEnter={(e) => {
            // MA-FUNNEL-ARCH-102: Subtle elevation hover for dark mode, not white inversion
            // Detect dark mode by checking if textPrimary is light/white
            const isDarkMode = BRAND.textPrimary?.includes('255,255,255') || 
                               BRAND.textPrimary?.includes('rgba(255') ||
                               BRAND.textPrimary?.startsWith('#fff') ||
                               BRAND.textPrimary?.startsWith('#FFF');
            if (isDarkMode) {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.95)';
            } else {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = BRAND.border;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = BRAND.border;
            e.currentTarget.style.color = BRAND.textSecondary;
          }}
        >
          ← Choose a different time
        </button>
      </div>
    </div>
  );
};

/**
 * Conflict Alert Component
 * MA-BOOKINGSTEP-APPEARANCE-FIX-034: Accept BRAND as prop
 */
const ConflictAlert = ({ message, onDismiss, BRAND }) => (
  <div 
    className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4 flex items-start gap-3"
    data-testid="conflict-alert"
  >
    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="text-amber-800 text-sm font-medium">{message}</p>
      <p className="text-amber-600 text-xs mt-0.5">Please choose another time below.</p>
    </div>
    <button 
      onClick={onDismiss} 
      className="text-amber-400 hover:text-amber-500 text-lg leading-none"
    >
      ×
    </button>
  </div>
);

/**
 * Main QuickBookingWidget Component
 * 
 * MA-BOOK-002: Removed silent fallback to hardcoded calendar/location IDs.
 * calendarId is now REQUIRED - if not provided, shows NOT_CONFIGURED state.
 */
export const QuickBookingWidget = ({
  // Required props
  contact,
  
  // MA-BOOK-002: calendarId is effectively REQUIRED
  // No fallback - if missing, widget shows NOT_CONFIGURED state
  calendarId,
  locationId,
  timezone: timezoneProp = DEFAULT_CONFIG.timezone,
  appointmentTitle = DEFAULT_CONFIG.appointmentTitle,
  fallbackText = DEFAULT_CONFIG.fallbackText,
  
  // Display settings (MA-UI-097, MA-UI-100)
  heading = null,           // Custom heading above slots
  subheading = null,        // Custom subheading below heading
  showTimezone = true,      // Show timezone note at bottom
  showDuration = true,      // Show meeting duration on slots
  durationMinutes = null,   // MA-UI-100: Manual duration in minutes (not auto-detected)
  showCalendarTitle = false, // Show calendar name in header
  calendarTitle = null,     // Calendar name to display
  
  // Funnel context (MA-ARCH-082 Phase 2)
  // When provided, emits booking events to funnel runtime
  funnelContext = null, // { funnel_id, session_id, node_id }
  
  // Callbacks
  onBookingSuccess,
  onBookingError,
  onNextStep, // Called with next_step_id from booking event routing
  
  // Embed flag
  embedded = false,
  
  // MA-PPB-FUNNEL-062: Theme tokens from funnel
  tokens = {},
  accentColor = null,  // MA-FUNNEL-DARK-THEME-FIX-033: Accept accent color for theme resolution
  
  // MA-FUNNEL-ARCH-073: Preview mode for Canvas/PPB surfaces
  // When true: uses mockSlots, disables booking actions, skips API calls
  preview = false,
  mockSlots = null, // { today: [...], tomorrow: [...] } - same shape as API response
  
  // MA-FUNNEL-ARCH-077: Compact mode for PPB miniature preview
  // When true: uses miniature text sizes (text-[6px], text-[7px], etc.) to match other PPB steps
  compact = false,
  
  // MA-FUNNEL-ARCH-076: Canvas editability via render props
  // Optional custom renderers for text elements (Canvas only)
  // When not provided, uses default text rendering (Runtime/PPB)
  renderHeading = null,        // (heading, defaultRender) => React.Node
  renderSubheading = null,     // (subheading, defaultRender) => React.Node
  renderSectionHeader = null,  // (label, section, defaultRender) => React.Node
  renderSectionSubtitle = null, // (subtitle, section, defaultRender) => React.Node
}) => {
  // MA-FUNNEL-DARK-THEME-FIX-033: Derive theme-aware BRAND from tokens
  const BRAND = getBrandColors(tokens, accentColor);
  
  // [MA-FUNNEL-ARCH-084] AUDIT: Log actual tokens received in compact mode
  if (compact && embedded) {
    console.log('[MA-FUNNEL-ARCH-084] PPB Dark Theme Audit - Tokens Received:', {
      'tokens.containerBg': tokens?.containerBg,
      'tokens.cardBg': tokens?.cardBg,
      'tokens.threadBg': tokens?.threadBg,
      'tokens.botBubbleBg': tokens?.botBubbleBg,
      'tokens.surface_elevated': tokens?.surface_elevated,
      'tokens.surface_base': tokens?.surface_base,
      'tokens.textPrimary': tokens?.textPrimary,
      'tokens.textSecondary': tokens?.textSecondary,
      'tokens.textMuted': tokens?.textMuted,
      'tokens.accent': tokens?.accent,
      'tokens.accentSoft': tokens?.accentSoft,
      'tokens.surfaceMode': tokens?.surfaceMode,
      'tokens.theme': tokens?.theme,
      'accentColor': accentColor,
      'compact': compact,
      'embedded': embedded,
      'preview': preview,
    });
  }
  
  // [MA-FUNNEL-ARCH-085/087] Fix theme resolution for PPB and Canvas dark mode
  // Check for any token that indicates embedded mode with theme data
  const baseTheme = embedded && (tokens.botBubbleBg || tokens.containerColor || tokens.threadColor || tokens.cardBg || tokens.textPrimary) ? {
    // MA-FUNNEL-ARCH-095: Light mode MUST use true card surfaces, NOT botBubble/container
    // Light mode: cardBg → surface_base → surface1 → #ffffff (canonical card surfaces)
    // Dark mode: botBubbleBg → containerColor → cardBg → #111827 (darker surfaces)
    cardBg: (() => {
      const isLightMode = tokens.surfaceMode !== 'dark' && 
                          tokens.theme !== 'dark' && 
                          !tokens.textPrimary?.includes('255,255,255') &&
                          !tokens.textMuted?.includes('255,255,255');
      
      // MA-FUNNEL-ARCH-095: In light mode, ONLY use card/surface tokens (no botBubble/container)
      return isLightMode
        ? (tokens.cardBg || tokens.surface_base || tokens.surface1 || '#ffffff')
        : (tokens.botBubbleBg || tokens.containerColor || tokens.cardBg || '#111827');
    })(),
    // Derive textPrimary: if textMuted is light (contains white), we're in dark mode
    textPrimary: tokens.textMuted?.includes('255,255,255') ? 'rgba(255,255,255,0.95)' : (tokens.textPrimary || '#1F2937'),
    textMuted: tokens.textMuted || tokens.textSecondary || '#6B7280',
    // Use accentColor prop or fallback to userColor/accent token
    accent: accentColor || tokens.userColor || tokens.accent || '#3B82F6',
    accentBorder: accentColor || tokens.userColor || tokens.accent || '#3B82F6',
    accentBg: tokens.accentSoft || `${accentColor || tokens.userColor || tokens.accent || '#3B82F6'}10`,
    accentBadgeBg: tokens.accentSoft || `${accentColor || tokens.userColor || tokens.accent || '#3B82F6'}14`,
    accentBadgeBorder: tokens.accentBorder || `${accentColor || tokens.userColor || tokens.accent || '#3B82F6'}80`,
    radius: tokens.radiusLg || '16px',
    slotRadius: '10px',
  } : {
    cardBg: BRAND.cardBg,
    textPrimary: BRAND.textPrimary,
    textMuted: BRAND.textSecondary,  // MA-FUNNEL-DARK-THEME-FIX-033: Use textSecondary consistently
    accent: BRAND.primary,
    accentBorder: BRAND.primary,
    accentBg: BRAND.primaryLight,
    accentBadgeBg: BRAND.primaryLight,
    accentBadgeBorder: `${BRAND.primary}80`,
    radius: tokens?.radiusLg || '16px',  // MA-FUNNEL-ARCH-083: Match outer Runtime wrapper (16px), not 24px
    slotRadius: '10px',
  };
  
  // MA-FUNNEL-ARCH-091: Detect dark mode for derived slot surfaces
  // Check surfaceMode token first, fallback to text color detection
  const isDark = tokens.surfaceMode === 'dark' || 
                 tokens.theme === 'dark' ||
                 baseTheme.textPrimary?.includes('255,255,255') || 
                 baseTheme.textPrimary?.includes('rgba(255') ||
                 baseTheme.textMuted?.includes('255,255,255');
  
  // MA-FUNNEL-ARCH-091/096/098: Derive slot surfaces
  // Dark mode: lighten from cardBg (correct blue-gray family)
  // Light mode: use explicit cool surface tokens (NOT darken from white)
  // MA-FUNNEL-ARCH-098: Fallback to #f8fafc (matches FB Test Page surface2)
  const theme = {
    ...baseTheme,
    slotBg: isDark 
      ? lighten(baseTheme.cardBg, 6)  // Dark: lighten(#111827, 6%) → #202736 ✅
      : (tokens.surface2 || tokens.surface_elevated || tokens.elevatedBg || '#f8fafc'),  // Light: soft near-white
    selectedSlotBg: tokens.accentSoft || `${accentColor || tokens.userColor || tokens.accent || '#3B82F6'}15`,
  };
  
  // [MA-FUNNEL-ARCH-084] AUDIT: Log resolved theme for PPB
  if (compact && embedded) {
    console.log('[MA-FUNNEL-ARCH-099] PPB Funnel Step Booking Background Audit:', {
      surface: 'PPB Funnel Step',
      compact,
      preview,
      embedded,
      isDark,
      bookingTheme: {
        cardBg: theme.cardBg,
        slotBg: theme.slotBg,
        selectedSlotBg: theme.selectedSlotBg,
      },
      tokensReceived: {
        headerBg: tokens?.headerBg,
        responseBg: tokens?.responseBg,
        userSectionBackground: tokens?.userSectionBackground,
        containerColor: tokens?.containerColor,
        containerBg: tokens?.containerBg,
        cardBg: tokens?.cardBg,
        surface1: tokens?.surface1,
        surface2: tokens?.surface2,
        surface_base: tokens?.surface_base,
        threadBg: tokens?.threadBg,
        threadColor: tokens?.threadColor,
        botBubbleBg: tokens?.botBubbleBg,
        messageThreadBackground: tokens?.messageThreadBackground,
        backgroundColor: tokens?.backgroundColor,
        surfaceMode: tokens?.surfaceMode,
        textPrimary: tokens?.textPrimary,
        textMuted: tokens?.textMuted,
      },
      cardBgResolution: {
        isLightMode: !isDark,
        selectedToken: baseTheme.cardBg,
        tokenChain: 'Light: cardBg → surface_base → surface1 → #ffffff',
      },
    });
  }
  
  // [MA-FUNNEL-ARCH-087] AUDIT: Log resolved theme for Canvas
  if (!compact && embedded && preview) {
    console.log('[MA-FUNNEL-ARCH-099] FB Canvas Booking Background Audit:', {
      surface: 'FB Canvas',
      compact,
      preview,
      embedded,
      isDark,
      bookingTheme: {
        cardBg: theme.cardBg,
        slotBg: theme.slotBg,
        selectedSlotBg: theme.selectedSlotBg,
      },
      tokensReceived: {
        headerBg: tokens?.headerBg,
        responseBg: tokens?.responseBg,
        userSectionBackground: tokens?.userSectionBackground,
        containerColor: tokens?.containerColor,
        containerBg: tokens?.containerBg,
        cardBg: tokens?.cardBg,
        surface1: tokens?.surface1,
        surface2: tokens?.surface2,
        surface_base: tokens?.surface_base,
        threadBg: tokens?.threadBg,
        threadColor: tokens?.threadColor,
        botBubbleBg: tokens?.botBubbleBg,
        messageThreadBackground: tokens?.messageThreadBackground,
        backgroundColor: tokens?.backgroundColor,
        surfaceMode: tokens?.surfaceMode,
        textPrimary: tokens?.textPrimary,
        textMuted: tokens?.textMuted,
      },
      cardBgResolution: {
        isLightMode: !isDark,
        selectedToken: baseTheme.cardBg,
        tokenChain: 'Light: cardBg → surface_base → surface1 → #ffffff',
      },
    });
  }
  
  // [MA-FUNNEL-ARCH-097] AUDIT: Log resolved theme for FB Test Page (Runtime)
  if (!compact && embedded && !preview) {
    console.log('[MA-FUNNEL-ARCH-099] FB Test Page Booking Background Audit:', {
      surface: 'FB Test Page',
      compact,
      preview,
      embedded,
      isDark,
      bookingTheme: {
        cardBg: theme.cardBg,
        slotBg: theme.slotBg,
        selectedSlotBg: theme.selectedSlotBg,
      },
      tokensReceived: {
        headerBg: tokens?.headerBg,
        responseBg: tokens?.responseBg,
        userSectionBackground: tokens?.userSectionBackground,
        containerColor: tokens?.containerColor,
        containerBg: tokens?.containerBg,
        cardBg: tokens?.cardBg,
        surface1: tokens?.surface1,
        surface2: tokens?.surface2,
        surface_base: tokens?.surface_base,
        threadBg: tokens?.threadBg,
        threadColor: tokens?.threadColor,
        botBubbleBg: tokens?.botBubbleBg,
        messageThreadBackground: tokens?.messageThreadBackground,
        backgroundColor: tokens?.backgroundColor,
        surfaceMode: tokens?.surfaceMode,
        textPrimary: tokens?.textPrimary,
        textMuted: tokens?.textMuted,
      },
      cardBgResolution: {
        isLightMode: !isDark,
        selectedToken: baseTheme.cardBg,
        tokenChain: 'Light: cardBg → surface_base → surface1 → #ffffff',
      },
    });
  }
  
  // [MA-FUNNEL-ARCH-090] AUDIT: Log resolved theme for FB Test Page Runtime
  if (!compact && embedded && !preview) {
    console.log('[MA-FUNNEL-ARCH-091] FB Test Page Runtime Audit - Derived Slot Surfaces:', {
      'isDark': isDark,
      'baseTheme.cardBg': baseTheme.cardBg,
      'theme.slotBg (derived)': theme.slotBg,
      'theme.selectedSlotBg': theme.selectedSlotBg,
      'derivation': isDark ? 'lighten(cardBg, 6%)' : 'darken(cardBg, 4%)',
      'theme.textPrimary': theme.textPrimary,
      'theme.textMuted': theme.textMuted,
      'theme.accent': theme.accent,
      'theme.accentBg': theme.accentBg,
      'theme.accentBorder': theme.accentBorder,
      'embedded': embedded,
      'preview': preview,
      'compact': compact,
      'tokens.surfaceMode': tokens?.surfaceMode,
      'tokens.theme': tokens?.theme,
      'tokens.cardBg': tokens?.cardBg,
      'tokens.botBubbleBg': tokens?.botBubbleBg,
      'tokens.textPrimary': tokens?.textPrimary,
      'tokens.textMuted': tokens?.textMuted,
      'tokens.accentSoft': tokens?.accentSoft,
      'accentColorProp': accentColor,
    });
  }

  // DEBUG: Log theme resolution for border-radius issue
  if (embedded) {
    console.log('[QuickBookingWidget] BORDER-RADIUS DEBUG:', {
      embedded,
      hasTokensAccent: !!tokens?.accent,
      tokensRadiusLg: tokens?.radiusLg,
      themeRadius: theme.radius,
      themePathUsed: (embedded && tokens.accent) ? 'PRIMARY (tokens.accent exists)' : 'FALLBACK (no tokens.accent)',
      themeCardBg: theme.cardBg,
      tokensContainerBg: tokens?.containerBg,
      allTokens: tokens,
    });
  }

  
  // MA-FUNNEL-ARCH-078: Complete sizing configuration for compact mode
  // Compact mode (PPB): Dense layout matching other miniature funnel steps
  // Normal mode (Canvas/Runtime): Spacious readable layout
  
  const sizes = compact ? {
    // Typography
    heading: 'text-[10px]',  // Increased from 8px for better prominence
    subheading: 'text-[8px]',  // Increased from 6px for better readability
    sectionHeader: 'text-[7px]',
    timeSlot: 'text-[7px]',
    duration: 'text-[5px]',
    recommended: 'text-[4px]',
    timezone: 'text-[5px]',
    // Icons
    calendarIcon: 'h-2 w-2',
    clockIcon: 'w-2.5 h-2.5',
    chevronIcon: 'h-2 w-2',
    // Layout & Spacing (CRITICAL for density)
    containerPadding: 'py-1.5',      // Removed px-2 for proper centering in compact mode
    sectionPadding: 'px-2 pb-1',          // vs 'px-4 py-3'
    slotPadding: '4px 6px',               // vs '10px 12px'
    gap: 'gap-1',                         // Icon-to-text spacing in section headers
    slotGap: 'gap-0.5',                   // vs 'gap-2'
    slotSpacing: 'space-y-1',             // vs 'space-y-2'
    sectionSpacing: 'mb-1',               // vs 'mb-3'
    headerSpacing: 'mb-0.5',              // vs 'mb-1'
    borderRadius: 'rounded-md',           // vs 'rounded-xl'
    slotRadius: '6px',                    // vs '10px'
    cardRadius: 'rounded-lg',             // vs 'rounded-2xl'
  } : {
    // Typography - MA-FUNNEL-ARCH-083: Refined hierarchy (heading > subheading > section)
    heading: 'text-base font-semibold',
    subheading: 'text-xs',
    sectionHeader: 'text-xs',
    timeSlot: 'text-sm',
    duration: 'text-xs',
    recommended: 'text-xs',
    timezone: 'text-xs',
    // Icons
    calendarIcon: 'w-[18px] h-[18px]',
    clockIcon: 'w-4 h-4',
    chevronIcon: 'w-4 h-4',
    // Layout & Spacing
    containerPadding: 'px-4 py-2',
    sectionPadding: 'px-4 py-3',
    slotPadding: '8px 10px',  // MA-FUNNEL-ARCH-083: Slightly reduced for better density
    gap: 'gap-2',  // MA-FUNNEL-ARCH-082: Section header icon-to-text spacing (normal Canvas mode)
    slotGap: 'gap-2',
    slotSpacing: 'space-y-2',
    sectionSpacing: 'mb-5',  // MA-FUNNEL-ARCH-083: Increased spacing between sections for natural breathing room
    headerSpacing: 'mb-1',
    borderRadius: 'rounded-xl',
    slotRadius: '10px',
    cardRadius: 'rounded-2xl',
  };
  
  // MA-FUNNEL-ARCH-076: Default renderers for text elements
  // Used when render props not provided (Runtime/PPB)
  const defaultHeadingRender = (text) => (
    <h3 
      className={`${sizes.heading} font-semibold mb-1`}
      style={{ color: theme.textPrimary }}
      data-testid="booking-heading"
    >
      {text}
    </h3>
  );
  
  const defaultSubheadingRender = (text) => (
    <p 
      className={`${sizes.subheading} mt-0`}
      style={{ color: theme.textMuted }}
      data-testid="booking-subheading"
    >
      {text}
    </p>
  );
  
  const defaultSectionHeaderRender = (label, isToday) => (
    <h3 
      className={`flex items-center ${sizes.gap} ${sizes.sectionHeader} ${isToday ? 'font-bold' : 'font-medium'}`}
      style={{ color: theme.textPrimary }}
    >
      <Calendar 
        className={`${isToday ? sizes.calendarIcon : sizes.calendarIcon} flex-shrink-0`}
        style={{ color: isToday ? theme.accent : theme.textMuted }} 
      />
      {label}
    </h3>
  );
  
  const defaultSectionSubtitleRender = (text) => (
    <p 
      className={`${compact ? 'text-[6px]' : sizes.timezone} ${compact ? 'mt-0.5 mb-0.5 ml-[13px]' : 'mt-0 ml-[26px]'}`}
      style={{ color: theme.textMuted }}
    >
      {text}
    </p>
  );

  // Resolve timezone
  const timezone = timezoneProp || getBrowserTimezone();
  
  // MA-UI-100: Format duration text from manual config
  const durationText = durationMinutes ? `${durationMinutes} min` : null;
  
  // MA-BOOK-002: Check if calendar is configured
  // MA-FUNNEL-ARCH-073: In preview mode, skip calendar check
  const isCalendarConfigured = preview || Boolean(calendarId && calendarId.trim());
  
  // State - MA-BOOK-002: Initialize to NOT_CONFIGURED if no calendar
  // MA-FUNNEL-ARCH-073: In preview mode with mockSlots, start with SLOTS state
  const [widgetState, setWidgetState] = useState(
    preview && mockSlots 
      ? WIDGET_STATE.SLOTS
      : (isCalendarConfigured ? WIDGET_STATE.LOADING : WIDGET_STATE.NOT_CONFIGURED)
  );
  
  // MA-FUNNEL-ARCH-073: Initialize slots from mockSlots if in preview mode
  // Process mock data same way as API response (today_slots/tomorrow_slots → today/tomorrow)
  const [slots, setSlots] = useState(
    preview && mockSlots 
      ? {
          today: mockSlots.today_slots || [],
          tomorrow: mockSlots.tomorrow_slots || [],
        }
      : { today: [], tomorrow: [] }
  );
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [conflictMessage, setConflictMessage] = useState(null);
  const [fallbackUrl, setFallbackUrl] = useState(null);
  const [error, setError] = useState(null);
  
  /**
   * Fetch available slots from API
   * MA-BOOK-002: Only fetch if calendarId is configured - NO FALLBACK
   * MA-FUNNEL-ARCH-073: Skip API fetch in preview mode
   */
  const fetchSlots = useCallback(async () => {
    // MA-FUNNEL-ARCH-073: Skip API fetch in preview mode
    if (preview) return;
    
    // MA-BOOK-002: Do not fetch if calendar is not configured
    if (!calendarId || !calendarId.trim()) {
      setWidgetState(WIDGET_STATE.NOT_CONFIGURED);
      setError('Booking calendar not configured');
      return;
    }
    
    setWidgetState(WIDGET_STATE.LOADING);
    setError(null);
    setConflictMessage(null);
    
    try {
      const params = new URLSearchParams({
        timezone,
        ...(locationId && { location_id: locationId }),
      });
      
      const response = await fetch(
        `${API_URL}/api/booking/slots/${calendarId}?${params}`
      );
      
      if (!response.ok) {
        throw new Error('Unable to load available times');
      }
      
      const data = await response.json();
      
      setSlots({
        today: data.today_slots || [],
        tomorrow: data.tomorrow_slots || [],
      });
      
      const totalSlots = (data.today_slots?.length || 0) + (data.tomorrow_slots?.length || 0);
      
      if (totalSlots === 0) {
        setWidgetState(WIDGET_STATE.NO_SLOTS);
      } else {
        setWidgetState(WIDGET_STATE.SLOTS);
      }
      
    } catch (err) {
      console.error('[QuickBooking] Fetch slots error:', err);
      setError(err.message || 'Unable to load available times');
      setWidgetState(WIDGET_STATE.ERROR);
    }
  }, [calendarId, locationId, timezone]);
  
  /**
   * Fetch fallback calendar URL
   */
  const fetchFallbackUrl = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        ...(locationId && { location_id: locationId }),
      });
      
      const response = await fetch(
        `${API_URL}/api/booking/calendar/${calendarId}/fallback-url?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setFallbackUrl(data.fallback_url);
      }
    } catch (err) {
      console.error('[QuickBooking] Fetch fallback URL error:', err);
    }
  }, [calendarId, locationId]);
  
  /**
   * Emit booking event to funnel runtime (MA-ARCH-082 Phase 2)
   * 
   * Called after a successful/failed booking when funnelContext is provided.
   * Includes idempotency via event_id to prevent duplicate processing.
   */
  const emitBookingEvent = useCallback(async (eventType, ghlAppointment = null, abandonmentReason = null) => {
    if (!funnelContext) return null;
    
    const { funnel_id, session_id, node_id } = funnelContext;
    if (!funnel_id || !session_id || !node_id) {
      console.warn('[QuickBooking] Missing funnel context fields, skipping event emission');
      return null;
    }
    
    // Generate idempotent event_id based on session, node, and booking
    const appointmentId = ghlAppointment?.appointment_id || ghlAppointment?.id || 'none';
    const eventId = `evt_${session_id}_${node_id}_${appointmentId}_${Date.now()}`;
    
    try {
      console.log('[QuickBooking] Emitting booking event:', { eventType, eventId, funnel_id, session_id, node_id });
      
      const response = await fetch(
        `${API_URL}/api/funnels/${funnel_id}/sessions/${session_id}/nodes/${node_id}/events/booking`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_id: eventId,
            event_type: eventType,
            ghl_appointment: ghlAppointment,
            abandonment_reason: abandonmentReason,
          }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[QuickBooking] Booking event failed:', errorData);
        return null;
      }
      
      const result = await response.json();
      console.log('[QuickBooking] Booking event result:', result);
      
      // Trigger navigation to next step if available
      if (result.next_step_id && onNextStep) {
        onNextStep(result.next_step_id);
      }
      
      return result;
    } catch (err) {
      console.error('[QuickBooking] Error emitting booking event:', err);
      return null;
    }
  }, [funnelContext, onNextStep]);
  
  /**
   * Handle slot selection - switch to confirmation view
   * MA-FUNNEL-ARCH-073: In preview mode, disable booking actions
   */
  const handleSlotSelect = (slot) => {
    if (preview) return; // No-op in preview mode
    setSelectedSlot(slot);
    setWidgetState(WIDGET_STATE.CONFIRMING);
  };
  
  /**
   * Handle cancel confirmation - go back to slots view
   */
  const handleCancelConfirmation = () => {
    setSelectedSlot(null);
    setWidgetState(WIDGET_STATE.SLOTS);
  };
  
  /**
   * Handle booking confirmation
   */
  const handleConfirmBooking = async () => {
    if (!selectedSlot || !contact) return;
    
    setIsBooking(true);
    setConflictMessage(null);
    
    try {
      const params = new URLSearchParams({
        ...(locationId && { location_id: locationId }),
      });
      
      const response = await fetch(
        `${API_URL}/api/booking/appointments?${params}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            calendar_id: calendarId,
            slot_start_time: selectedSlot.start_time,
            contact: {
              first_name: contact.first_name,
              last_name: contact.last_name,
              email: contact.email,
              phone: contact.phone,
              contact_id: contact.contact_id || null,
            },
            timezone,
            title: appointmentTitle,
            // MA-FUNNEL-LEAD-001: Pass funnel context for dispatch logging
            session_id: funnelContext?.session_id || null,
            funnel_id: funnelContext?.funnel_id || null,
            funnel_name: funnelContext?.funnel_name || null,
            lead_id: funnelContext?.lead_id || null,
            journey_id: funnelContext?.journey_id || null,
            tenant_id: funnelContext?.tenant_id || null,
            booking_node_id: funnelContext?.node_id || null,
          }),
        }
      );
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('[QuickBooking] Failed to parse response:', parseError);
        throw new Error('Unable to complete booking');
      }
      
      if (!response.ok) {
        if (response.status === 409) {
          const errorDetail = data.detail || {};
          const errorMessage = typeof errorDetail === 'string' 
            ? errorDetail 
            : errorDetail.message || 'That time was just taken. Please choose another time.';
          setConflictMessage(errorMessage);
          setSelectedSlot(null);
          setWidgetState(WIDGET_STATE.SLOTS);
          await fetchSlots();
          return;
        }
        
        const errorMessage = typeof data.detail === 'string' 
          ? data.detail 
          : data.detail?.message || 'Unable to complete booking';
        throw new Error(errorMessage);
      }
      
      setBookingResult({
        displayDate: data.display_date,
        displayTime: data.display_time,
        appointmentId: data.appointment_id,
      });
      setWidgetState(WIDGET_STATE.SUCCESS);
      
      // Emit booking_completed event to funnel runtime (MA-ARCH-082)
      // Transform the response to GHL appointment format for the event
      const ghlAppointment = {
        id: data.appointment_id,
        calendarId: calendarId,
        startTime: selectedSlot.start_time,
        endTime: selectedSlot.end_time,
        duration: 30, // Default duration, actual from slot if available
        timezone: timezone,
        contactId: data.contact_id,
        title: appointmentTitle,
      };
      await emitBookingEvent('booking_completed', ghlAppointment);
      
      if (onBookingSuccess) {
        onBookingSuccess({
          appointmentId: data.appointment_id,
          contactId: data.contact_id,
          startTime: selectedSlot.start_time,
          displayDate: data.display_date,
          displayTime: data.display_time,
        });
      }
      
    } catch (err) {
      console.error('[QuickBooking] Booking error:', err);
      setError(err.message || 'Unable to complete booking');
      setWidgetState(WIDGET_STATE.ERROR);
      
      if (onBookingError) {
        onBookingError(err);
      }
    } finally {
      setIsBooking(false);
    }
  };
  
  /**
   * Load slots and fallback URL on mount
   */
  useEffect(() => {
    fetchSlots();
    fetchFallbackUrl();
  }, [fetchSlots, fetchFallbackUrl]);
  
  /**
   * Post widget height to parent window for iframe auto-resize
   * Sends multiple message types for maximum compatibility (GHL, Safari, custom embeds)
   */
  const widgetRef = useRef(null);
  const lastPostedHeight = useRef(0);
  
  const postHeightToParent = useCallback(() => {
    if (!widgetRef.current) return;
    
    // Calculate widget's own content height (not page/body height)
    const contentHeight = widgetRef.current.scrollHeight;
    const offsetHeight = widgetRef.current.offsetHeight;
    
    // Use the larger of widget's scrollHeight and offsetHeight
    // Add generous padding (80px) to ensure bottom content like "View full calendar" isn't cut off
    const height = Math.max(contentHeight, offsetHeight) + 80;
    
    // Only post if height changed significantly (avoid jitter)
    if (Math.abs(height - lastPostedHeight.current) < 10) return;
    lastPostedHeight.current = height;
    
    // Post to parent window if in iframe
    if (window.parent !== window) {
      try {
        // Standard widget height message
        window.parent.postMessage({
          type: 'WIDGET_HEIGHT',
          payload: { height }
        }, '*');
        
        // Also send FUNNEL_RESIZE for ConversationFunnelEmbed compatibility
        window.parent.postMessage({
          type: 'FUNNEL_RESIZE',
          payload: { height }
        }, '*');
        
        // GHL-specific message format
        window.parent.postMessage({
          type: 'setHeight',
          height: height
        }, '*');
      } catch (e) {
        console.warn('[QuickBooking] Failed to post height:', e);
      }
    }
  }, []);
  
  // Post height on state changes and after renders
  useEffect(() => {
    // Initial post
    postHeightToParent();
    
    // Post after render settles
    const timers = [
      setTimeout(postHeightToParent, 50),
      setTimeout(postHeightToParent, 150),
      setTimeout(postHeightToParent, 300),
      setTimeout(postHeightToParent, 500),
    ];
    
    return () => timers.forEach(clearTimeout);
  }, [widgetState, slots, bookingResult, selectedSlot, postHeightToParent]);
  
  // ResizeObserver for dynamic content changes
  useEffect(() => {
    if (!widgetRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      postHeightToParent();
    });
    
    resizeObserver.observe(widgetRef.current);
    
    // Also observe body for any layout changes
    resizeObserver.observe(document.body);
    
    return () => resizeObserver.disconnect();
  }, [postHeightToParent]);
  
  // Periodic height check for Safari/GHL compatibility
  useEffect(() => {
    const interval = setInterval(postHeightToParent, 500);
    return () => clearInterval(interval);
  }, [postHeightToParent]);
  
  // Get timezone display (null if uncommon)
  const timezoneDisplay = getTimezoneDisplayName(timezone);
  
  // Separate soonest slot from other today slots
  const soonestSlot = slots.today.length > 0 ? slots.today[0] : null;
  const otherTodaySlots = slots.today.length > 1 ? slots.today.slice(1) : [];
  const hasTodaySlots = slots.today.length > 0;
  
  return (
    <div 
      ref={widgetRef}
      className="w-full"
      style={{
        // MA-FUNNEL-ARCH-080: Compact mode applies max-width constraint for PPB miniaturization
        maxWidth: compact ? '220px' : '100%',
        margin: compact ? '0 auto' : '0',  // Center the widget in compact mode
        // MA-FUNNEL-ARCH-083: Remove padding when embedded so inner card aligns with outer wrapper's rounded corners
        padding: compact ? '4px 0 6px' : (embedded ? '0' : '0 16px 24px'),  // More headspace at top
        boxSizing: 'border-box',
        background: 'transparent',
      }}
      data-testid="quick-booking-widget-container"
    >
      {/* Inner card with premium styling */}
      <div
        className="w-full relative"
        style={{
          // MA-FUNNEL-ARCH-083: For PPB compact mode, remove container styling to fill frame
          // MA-FUNNEL-ARCH-091: Transparent background when embedded so card matches parent container
          backgroundColor: (compact || embedded) ? 'transparent' : theme.cardBg,
          borderRadius: compact ? '0' : theme.radius, // Widget keeps rounded corners in Canvas
          // MA-FUNNEL-ARCH-080: Compact mode uses miniature padding for PPB scale
          // MA-FUNNEL-ARCH-091: No bottom padding when embedded to create gap with parent
          padding: compact ? '5px 0 9px' : (embedded ? '16px 12px 0 12px' : '24px'),  // No bottom padding for Canvas gap
          boxShadow: (embedded || compact) ? 'none' : '0 12px 30px rgba(15,23,42,0.08), 0 0 0 1px rgba(99,179,237,0.10), 0 0 24px rgba(99,179,237,0.08)',
          border: 'none',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'hidden',
        }}
        data-testid="quick-booking-widget"
      >
      {/* Loading State */}
      {widgetState === WIDGET_STATE.LOADING && <LoadingState BRAND={BRAND} />}
      
      {/* MA-BOOK-002: Not Configured State - Calendar not assigned */}
      {widgetState === WIDGET_STATE.NOT_CONFIGURED && (
        <NotConfiguredState BRAND={BRAND} />
      )}
      
      {/* Error State */}
      {widgetState === WIDGET_STATE.ERROR && (
        <ErrorState message={error} onRetry={fetchSlots} BRAND={BRAND} />
      )}
      
      {/* No Slots State */}
      {widgetState === WIDGET_STATE.NO_SLOTS && (
        <NoSlotsState fallbackUrl={fallbackUrl} fallbackText={fallbackText} BRAND={BRAND} />
      )}
      
      {/* Success State */}
      {widgetState === WIDGET_STATE.SUCCESS && bookingResult && (
        <SuccessState
          displayDate={bookingResult.displayDate}
          displayTime={bookingResult.displayTime}
          appointmentTitle={appointmentTitle}
          BRAND={BRAND}
        />
      )}
      
      {/* Slots View */}
      {widgetState === WIDGET_STATE.SLOTS && (
        <div className={sizes.containerPadding}>
          {/* Custom Heading/Subheading (MA-UI-097) */}
          {/* MA-FUNNEL-ARCH-076: Use render props if provided (Canvas), otherwise default */}
          {(heading || subheading) && (
            <div className={`${sizes.sectionSpacing} ${compact ? 'mb-2' : ''} text-center`}>
              {heading && (
                renderHeading 
                  ? renderHeading(heading, defaultHeadingRender)
                  : defaultHeadingRender(heading)
              )}
              {subheading && (
                renderSubheading
                  ? renderSubheading(subheading, defaultSubheadingRender)
                  : defaultSubheadingRender(subheading)
              )}
            </div>
          )}
          
          {/* Calendar Title (MA-UI-097) */}
          {showCalendarTitle && calendarTitle && (
            <div 
              className="text-center mb-2 pb-2 border-b border-gray-100"
              data-testid="calendar-title"
            >
              <span 
                className="text-xs font-medium"
                style={{ color: BRAND.textSecondary }}
              >
                {calendarTitle}
              </span>
            </div>
          )}
          
          {/* Conflict Alert */}
          {conflictMessage && (
            <ConflictAlert
              message={conflictMessage}
              onDismiss={() => setConflictMessage(null)}
              BRAND={BRAND}
            />
          )}
          
          {/* Today Section - Only if there are today slots */}
          {hasTodaySlots && (
            <div className={`${sizes.slotSpacing} ${sizes.sectionSpacing} ${compact ? 'flex flex-col items-center' : ''}`}>
              {/* Section Header - MA-FUNNEL-ARCH-076: Use render props if provided */}
              <div className={`${sizes.headerSpacing} w-full`}>
                {renderSectionHeader 
                  ? renderSectionHeader("Talk Today If You Can", "today", defaultSectionHeaderRender)
                  : defaultSectionHeaderRender("Talk Today If You Can", true)
                }
                {renderSectionSubtitle
                  ? renderSectionSubtitle("Best available option.", "today", defaultSectionSubtitleRender)
                  : defaultSectionSubtitleRender("Best available option.")
                }
              </div>
              
              {/* Primary/Recommended Slot */}
              {soonestSlot && (
                <PrimarySlotButton
                  slot={soonestSlot}
                  onSelect={handleSlotSelect}
                  disabled={isBooking}
                  durationText={showDuration ? durationText : null}
                  theme={theme}
                  sizes={sizes}
                />
              )}
              
              {/* Other Today Slots */}
              {otherTodaySlots.length > 0 && (
                <div className={`${sizes.slotSpacing} ${compact ? 'flex flex-col items-center w-full' : ''}`}>
                  {otherTodaySlots.map((slot) => (
                    <SecondarySlotButton
                      key={slot.slot_id}
                      slot={slot}
                      onSelect={handleSlotSelect}
                      disabled={isBooking}
                      durationText={showDuration ? durationText : null}
                      theme={theme}
                      sizes={sizes}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Tomorrow Section - MA-FUNNEL-ARCH-076: Use render props if provided */}
          {slots.tomorrow.length > 0 && (
            <div className={`${sizes.slotSpacing} ${sizes.sectionSpacing} ${compact ? 'flex flex-col items-center mt-2' : ''}`}>
              <div className={`${sizes.headerSpacing} w-full`}>
                {renderSectionHeader
                  ? renderSectionHeader("Tomorrow", "tomorrow", defaultSectionHeaderRender)
                  : defaultSectionHeaderRender("Tomorrow", false)
                }
              </div>
              
              <div className={`${sizes.slotSpacing} ${compact ? 'flex flex-col items-center w-full' : ''}`}>
                {slots.tomorrow.map((slot, index) => (
                  <SecondarySlotButton
                    key={slot.slot_id}
                    slot={slot}
                    onSelect={handleSlotSelect}
                    disabled={isBooking}
                    isRecommended={!hasTodaySlots && index === 0}
                    durationText={showDuration ? durationText : null}
                    theme={theme}
                    sizes={sizes}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Footer: Timezone note + Fallback link (MA-UI-097: Respects showTimezone) */}
          <div className={`${sizes.sectionSpacing} ${compact ? 'mt-3' : ''} text-center`}>
            {showTimezone && (
              <p 
                className={sizes.timezone}
                style={{ color: theme.textMuted }}
                data-testid="timezone-note"
              >
                {timezoneDisplay 
                  ? `Times shown in your local timezone (${timezoneDisplay}).`
                  : 'Times shown in your local timezone.'
                }
              </p>
            )}
            
            {fallbackUrl && !embedded && (
              <p className="text-xs" style={{ color: BRAND.textSecondary }}>
                Need another time?{' '}
                <a
                  href={fallbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:underline inline-flex items-center gap-0.5"
                  style={{ color: BRAND.primaryDark, fontSize: '13px' }}
                  data-testid="view-full-calendar-link"
                >
                  View full calendar
                  <svg 
                    className="w-3 h-3" 
                    viewBox="0 0 12 12" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 10L10 2M10 2H4M10 2V8" />
                  </svg>
                </a>
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Confirmation View - Inline confirmation (replaces slots) */}
      {widgetState === WIDGET_STATE.CONFIRMING && selectedSlot && (
        <ConfirmationView
          slot={selectedSlot}
          onConfirm={handleConfirmBooking}
          onCancel={handleCancelConfirmation}
          isBooking={isBooking}
          appointmentTitle={appointmentTitle}
          BRAND={BRAND}
        />
      )}
      </div>
    </div>
  );
};

export default QuickBookingWidget;
